import { Box, Button } from "@mui/material";
import StyleInfoCard from "./StyleInfoCard";
import SettingsSlider from "../../Shared/SettingsSlider";
import MarginBox from "../../Shared/MarginBox";
import { useDispatch, useSelector } from "react-redux";
import { getGlobalSettings } from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  pushToHistoryAction,
  setGlobalHeadingProps,
  setGlobalLineHeight,
  setGlobalParagraphProps,
} from "../../../../redux/reducers/segmentBuilder";
import { useState } from "react";
import {
  setGlobalCornerRounding,
  setGlobalSettings,
  setGlobalBorderThickness,
  setGlobalMargin,
} from "../../../../redux/actions/globalSettings";

const StyleTab = () => {
  const dispatch = useDispatch();

  const globalSettings = useSelector(getGlobalSettings);

  const [cornerRounding, setCornerRounding] = useState(
    globalSettings?.cornerRounding ?? 8
  );
  const [borderThickness, setBorderThickness] = useState(
    globalSettings?.borderThickness ?? 8
  );
  const [lineHeight, setLineHeight] = useState(
    globalSettings?.lineHeight ?? 1.15
  );
  const [margin, setMargin] = useState(
    globalSettings?.margin || { top: 20, bottom: 20, left: 20, right: 20 }
  );

  const [headingProps, setHeadingProps] = useState(
    globalSettings?.headingProps || {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 20,
      resetKey: 0,
    }
  );

  const [paragraphProps, setParagraphProps] = useState(
    globalSettings?.paragraphProps || {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 14,
      resetKey: 0,
    }
  );

  const handleGlobalSettingsChange = (newSettings = {}) => {
    dispatch(setGlobalSettings(newSettings));
    dispatch(pushToHistoryAction());
  };

  const handleMarginAdjustment = (side, operation) => {
    setMargin((prev) => {
      const oldVal = prev[side] || 0;
      const newVal = operation === "increase" ? oldVal + 1 : oldVal - 1;
      return {
        ...prev,
        [side]: Math.max(newVal, 0), // prevent negative
      };
    });
  };

  const handleCornerRoundingChange = (val) => {
    dispatch(setGlobalCornerRounding(val));
    dispatch(pushToHistoryAction());
    setCornerRounding(val);
  };

  const handleLineHeightChange = (val) => {
    dispatch(setGlobalLineHeight(val));
    dispatch(pushToHistoryAction());
    setLineHeight(val);
  };

  const handleBorderThicknessChange = (val) => {
    dispatch(setGlobalBorderThickness(val));
    dispatch(pushToHistoryAction());
    setBorderThickness(val);
  };
  const handleMarginChange = (newMargin) => {
    dispatch(setGlobalMargin(newMargin));
    dispatch(pushToHistoryAction());
  };

  const handleHeadingPropsChange = (value, key) => {
    if (key === "resetKey") {
      setHeadingProps((prev) => ({ ...prev, resetKey: prev.resetKey + 1 }));
    } else {
      setHeadingProps((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleApplyHeadingProps = (newProps) => {
    dispatch(setGlobalHeadingProps(newProps));
    dispatch(pushToHistoryAction());
    setHeadingProps(newProps); // Update local state to keep in sync
  };

  const handleParagraphPropsChange = (value, key) => {
    setParagraphProps((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyParagraphProps = (newProps) => {
    dispatch(setGlobalParagraphProps(newProps));
    dispatch(pushToHistoryAction());
    setParagraphProps(newProps);
  };

  const handleApplyAll = () => {
    dispatch(
      setGlobalSettings({
        cornerRounding,
        borderThickness,
        margin,
        headingProps,
        paragraphProps,
        lineHeight,
      })
    );
    dispatch(pushToHistoryAction());
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box
        sx={{
          padding: "12px 16px",
          backgroundColor: "#F0F4FF",
          borderRadius: "8px",
          border: "1px solid #E1E9FF",
          marginBottom: "8px",
        }}
      >
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: "500",
            color: "#4A5568",
            textAlign: "center",
          }}
        >
          Changes here will reflect across the entire Wheel.
        </Box>
      </Box>

      <StyleInfoCard
        title={"Header Text"}
        selectedFont={headingProps?.fontFamily}
        selectedColor={headingProps?.fontColor}
        handleFontChange={(font) =>
          handleHeadingPropsChange(font, "fontFamily")
        }
        inputValue={headingProps?.fontSize}
        handleInputValueChange={(size) =>
          handleHeadingPropsChange(size, "fontSize")
        }
        onConfirmCustomColor={(color) =>
          handleHeadingPropsChange(color, "fontColor")
        }
        handleApplyChanges={() => handleApplyHeadingProps(headingProps)}
        tooltip="Changes the settings for any text in the block that is formatted as a 'Header'"
        type="Header"
      />

      <StyleInfoCard
        title="Paragraph Text"
        selectedFont={paragraphProps?.fontFamily}
        selectedColor={paragraphProps?.fontColor}
        handleFontChange={(font) =>
          handleParagraphPropsChange(font, "fontFamily")
        }
        inputValue={paragraphProps?.fontSize}
        handleInputValueChange={(size) =>
          handleParagraphPropsChange(size, "fontSize")
        }
        onConfirmCustomColor={(color) =>
          handleParagraphPropsChange(color, "fontColor")
        }
        handleApplyChanges={() => handleApplyParagraphProps(paragraphProps)}
        tooltip="Changes the settings for any text in the block that is formatted as a 'Paragraph'"
        type={"Paragraph"}
      />

      <SettingsSlider
        title="Line Spacing"
        sliderValue={lineHeight}
        onValueChange={(val) => setLineHeight(val)}
        min={1}
        max={2}
        tooltip="Adjust the line spacing of the texts."
        containerStyle={{
          backgroundColor: "#F6F7F9",
          padding: "16px",
          borderRadius: "8px",
        }}
        applyButton
        handleApplyChanges={(val) => {
          handleLineHeightChange(val);
        }}
        step={0.05}
        allowDecimal
      />

      <SettingsSlider
        title="Corner Rounding"
        sliderValue={cornerRounding}
        onValueChange={(val) => setCornerRounding(val)}
        min={0}
        max={20}
        tooltip="Adjust the corner rounding of the element."
        containerStyle={{
          backgroundColor: "#F6F7F9",
          padding: "16px",
          borderRadius: "8px",
        }}
        applyButton
        handleApplyChanges={(val) => {
          handleCornerRoundingChange(val);
        }}
      />

      <SettingsSlider
        title="Border Thickness"
        sliderValue={borderThickness}
        onValueChange={(val) => setBorderThickness(val)}
        min={0}
        max={8}
        tooltip="Adjust the Border Thickness of the element."
        containerStyle={{
          backgroundColor: "#F6F7F9",
          padding: "16px",
          borderRadius: "8px",
        }}
        applyButton
        handleApplyChanges={(val) => {
          handleBorderThicknessChange(val);
        }}
      />

      <MarginBox
        margin={margin}
        increaseMargin={(side) => handleMarginAdjustment(side, "increase")}
        decreaseMargin={(side) => handleMarginAdjustment(side, "decrease")}
        isApplyMarginOnAllSides={false}
        handleMarginChange={(side, newValue) => {
          setMargin((prev) => ({
            ...prev,
            [side]: newValue,
          }));
        }}
        checkbox={false}
        containerStyle={{
          padding: "8px",
        }}
        AccordionContainerStyle={{
          backgroundColor: "#F6F7F9",
          padding: "8px",
        }}
        applyButton
        handleApplyChanges={() => {
          handleMarginChange(margin);
        }}
      />

      {/* <Divider sx={{ my: "10px", backgroundColor: "#98A2B3", height: "2px" }} /> */}

      <Box
        sx={{
          bottom: 0,
          position: "sticky",
          backgroundColor: "white",
          py: 3,
          zIndex: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.2,
          borderTop: "2px solid #98A2B3",
        }}
      >
        {/* <Button
          sx={{
            color: "#495057",
            textTransform: "capitalize",
            fontSize: "16px",
            fontWeight: "500",
          }}
          //   onClick={onClose}
        >
          Cancel
        </Button> */}
{/* 
        <Button
          onClick={handleApplyAll}
          sx={{
            color: "white",
            textTransform: "capitalize",
            fontSize: "16px",
            fontWeight: "500",
            background: "#9B5AF8",

            "&:hover": {
              background: "#9B5AF885",
            },
          }}
        >
          Apply All
        </Button> */}
      </Box>
    </Box>
  );
};

export default StyleTab;
